import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScrollPicker from './ScrollPicker';
import { LanguageToggleDark } from '@/components/feature/LanguageToggle';

type CalendarType = 'solar' | 'lunar' | 'lunarLeap';
type Gender = 'male' | 'female';

interface BirthDateInputProps {
  onNext: (data: { year: number; month: number; day: number; calendarType: CalendarType; gender: Gender }) => void;
  defaultYear?: number; defaultMonth?: number; defaultDay?: number;
  defaultCalendarType?: CalendarType; defaultGender?: Gender;
}

const currentYear = new Date().getFullYear();

function getDaysInMonth(year: number, month: number, calType: CalendarType): number {
  if (calType !== 'solar') return 30;
  return new Date(year, month, 0).getDate();
}

export default function BirthDateInput({ onNext, defaultYear = 1990, defaultMonth = 1, defaultDay = 1, defaultCalendarType = 'solar', defaultGender = 'male' }: BirthDateInputProps) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const [calendarType, setCalendarType] = useState<CalendarType>(defaultCalendarType);
  const [gender, setGender] = useState<Gender>(defaultGender);
  const [yearIdx, setYearIdx] = useState(defaultYear - 1900);
  const [monthIdx, setMonthIdx] = useState(defaultMonth - 1);
  const [dayIdx, setDayIdx] = useState(defaultDay - 1);

  const selectedYear = 1900 + yearIdx;
  const selectedMonth = monthIdx + 1;
  const daysCount = getDaysInMonth(selectedYear, selectedMonth, calendarType);

  const years = Array.from({ length: currentYear - 1899 }, (_, i) => isEn ? `${1900 + i}` : `${1900 + i}년`);
  const months = Array.from({ length: 12 }, (_, i) => isEn ? `${i + 1}` : `${i + 1}월`);
  const days = Array.from({ length: daysCount }, (_, i) => isEn ? `${i + 1}` : `${i + 1}일`);

  const safeDayIdx = Math.min(dayIdx, daysCount - 1);

  const calOpts: { type: CalendarType; labelKey: string }[] = [
    { type: 'solar', labelKey: 'date.solar' },
    { type: 'lunar', labelKey: 'date.lunar' },
    { type: 'lunarLeap', labelKey: 'date.lunarLeap' },
  ];

  const previewLabel = calendarType === 'solar' ? t('date.preview.solar') : calendarType === 'lunar' ? t('date.preview.lunar') : t('date.preview.lunarLeap');
  const previewDate = isEn
    ? `${selectedYear} / ${selectedMonth} / ${safeDayIdx + 1}`
    : `${selectedYear}년 ${selectedMonth}월 ${safeDayIdx + 1}일`;

  return (
    <div className="min-h-dvh flex flex-col bg-white" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* 헤더 고정 영역 */}
      <div className="px-5 pt-5 pb-0 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <img src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png" alt="바른사주" className="h-7 object-contain" />
          <LanguageToggleDark />
        </div>
        <div className="flex gap-1.5 mb-0">
          {[0,1,2,3].map(i => <div key={i} className={`h-1 flex-1 rounded-full ${i === 0 ? 'bg-gray-900' : 'bg-gray-200'}`} />)}
        </div>
      </div>

      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
        <div className="max-w-sm mx-auto w-full">
          <p className="text-gray-400 text-sm mb-1.5">{t('date.step')}</p>
          <h2 className="text-2xl font-bold text-gray-900 mb-5 leading-snug">{t('date.title')}</h2>

          {/* 성별 선택 */}
          <div className="flex gap-3 mb-4">
            {([{ value: 'female' as Gender, labelKey: 'date.female', icon: 'ri-women-line' }, { value: 'male' as Gender, labelKey: 'date.male', icon: 'ri-men-line' }]).map(opt => (
              <button key={opt.value} onClick={() => setGender(opt.value)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap border-2 ${gender === opt.value ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-400'}`}>
                <div className="w-5 h-5 flex items-center justify-center"><i className={`${opt.icon} text-base`} /></div>
                {t(opt.labelKey)}
              </button>
            ))}
          </div>

          {/* 음력/양력 선택 */}
          <div className="flex bg-gray-100 rounded-full p-1 mb-5 gap-1">
            {calOpts.map(opt => (
              <button key={opt.type} onClick={() => setCalendarType(opt.type)}
                className={`flex-1 py-2 px-3 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${calendarType === opt.type ? 'bg-white text-gray-900 font-bold' : 'text-gray-500 hover:text-gray-700'}`}>
                {t(opt.labelKey)}
              </button>
            ))}
          </div>

          {/* 날짜 피커 */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1">
              <p className="text-xs text-gray-400 text-center mb-1.5">{t('date.year')}</p>
              <ScrollPicker items={years} selectedIndex={yearIdx} onChange={setYearIdx} itemHeight={48} visibleCount={5} />
            </div>
            <div className="flex-none w-[4.5rem]">
              <p className="text-xs text-gray-400 text-center mb-1.5">{t('date.month')}</p>
              <ScrollPicker items={months} selectedIndex={monthIdx} onChange={setMonthIdx} itemHeight={48} visibleCount={5} />
            </div>
            <div className="flex-none w-[4.5rem]">
              <p className="text-xs text-gray-400 text-center mb-1.5">{t('date.day')}</p>
              <ScrollPicker items={days} selectedIndex={safeDayIdx} onChange={setDayIdx} itemHeight={48} visibleCount={5} />
            </div>
          </div>

          {/* 미리보기 */}
          <div className="bg-gray-50 rounded-2xl p-3.5 text-center">
            <span className="text-gray-500 text-sm">{previewLabel} </span>
            <span className="text-gray-900 font-bold text-base">{previewDate}</span>
          </div>
        </div>
      </div>

      {/* 고정 하단 버튼 */}
      <div className="flex-shrink-0 px-5 pt-3 bg-white border-t border-gray-100" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.25rem)' }}>
        <div className="max-w-sm mx-auto w-full">
          <button
            onClick={() => onNext({ year: selectedYear, month: selectedMonth, day: safeDayIdx + 1, calendarType, gender })}
            className="w-full bg-gray-900 text-white font-bold text-base py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-gray-800 active:scale-95 transition-all cursor-pointer whitespace-nowrap">
            {t('next')} <span className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-right-line text-sm" /></span>
          </button>
        </div>
      </div>
    </div>
  );
}
